import { useState } from 'react'
import { MoreHorizontal, Trash2, Copy, ExternalLink, Smile, Frown, Pin, PinOff } from 'lucide-react'
import { DeletePostConfirmModal } from '@/components/DeletePostConfirmModal'
import { useToast } from '@/hooks/useToast'
import { AT_EXPLORE_BASE_URL } from '@/constants/api'
import { strings } from '@/strings'

export interface PostActionsMenuProps {
    /** Icon size for the MoreHorizontal trigger button */
    iconSize?: 'sm' | 'md'
    /** Tailwind top-* class for dropdown positioning */
    dropdownTopClass?: string

    /** Delete post — shown when isOwnPost && canDelete */
    isOwnPost?: boolean
    canDelete?: boolean
    onDelete?: () => void

    /**
     * Pin/unpin to profile — shown only when the pin state is actually known.
     * Leave undefined wherever it is not (other tabs, other users, home timeline)
     * so the app never offers a blind re-pin.
     */
    pinState?: 'pinned' | 'unpinned'
    onTogglePin?: () => void

    /** Copy text — shown when text is provided */
    text?: string

    /** Copy URI — shown when showCopyUri is true */
    showCopyUri?: boolean
    uri?: string

    /** Open in AT Explore — shown when showAtExplore is true */
    showAtExplore?: boolean
    isActivityPub?: boolean
    authorHandle?: string
    rkey?: string

    /** Nostr event items — shown when nostrNevent is provided */
    nostrNevent?: string | null

    /**
     * "Show more/less like this" feed-tuning actions — shown only when provided.
     * Both callbacks are required members, so an inert menu row is unrepresentable.
     */
    feedTuning?: {
        busy: boolean
        onShowMore: () => void
        onShowLess: () => void
    } | null
}

export function PostActionsMenu({
    iconSize = 'sm',
    dropdownTopClass = 'top-6',
    isOwnPost = false,
    canDelete = false,
    pinState,
    onTogglePin,
    onDelete,
    text,
    showCopyUri = false,
    uri,
    showAtExplore = false,
    isActivityPub = false,
    authorHandle,
    rkey,
    nostrNevent,
    feedTuning,
}: PostActionsMenuProps) {
    const [showMenu, setShowMenu] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const { showToast } = useToast()

    const iconClass = iconSize === 'md' ? 'w-5 h-5' : 'w-4 h-4'

    const showDelete = isOwnPost && canDelete
    const showPin = !!pinState && !!onTogglePin
    const hasCopyOrExploreItems = !!text || showCopyUri || showAtExplore || !!nostrNevent
    const hasSubsequentItems = !!feedTuning || hasCopyOrExploreItems

    const handleAtExploreClick = () => {
        if (!authorHandle) return
        const url = isActivityPub
            ? `${AT_EXPLORE_BASE_URL}/ap/${authorHandle}/posts/${btoa(uri ?? '')
                  .replace(/\+/g, '-')
                  .replace(/\//g, '_')
                  .replace(/=/g, '')}?mode=simple`
            : `${AT_EXPLORE_BASE_URL}/${authorHandle}/app.bsky.feed.post/${rkey ?? uri?.split('/').pop()}?mode=simple`
        window.open(url, '_blank', 'noopener,noreferrer')
        setShowMenu(false)
    }

    return (
        <div className="relative flex-shrink-0">
            <button
                aria-label="More options"
                onClick={e => {
                    e.stopPropagation()
                    setShowMenu(prev => !prev)
                }}
                className="text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark p-1 rounded-full hover:bg-border/30 focus:outline-none"
            >
                <MoreHorizontal className={iconClass} />
            </button>

            {showMenu && (
                <>
                    <div
                        className="fixed inset-0 z-10"
                        onClick={e => {
                            e.stopPropagation()
                            setShowMenu(false)
                        }}
                    />
                    <div
                        className={`absolute right-0 ${dropdownTopClass} z-20 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg shadow-lg py-1 min-w-[140px] max-w-[calc(100vw-1rem)]`}
                    >
                        {showPin && (
                            <button
                                onClick={e => {
                                    e.stopPropagation()
                                    setShowMenu(false)
                                    onTogglePin()
                                }}
                                className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                {pinState === 'pinned' ? (
                                    <PinOff className="w-4 h-4 shrink-0" />
                                ) : (
                                    <Pin className="w-4 h-4 shrink-0" />
                                )}
                                {pinState === 'pinned'
                                    ? strings.posts.unpinFromProfile
                                    : strings.posts.pinToProfile}
                            </button>
                        )}

                        {showDelete && (
                            <button
                                onClick={e => {
                                    e.stopPropagation()
                                    setShowMenu(false)
                                    setShowDeleteConfirm(true)
                                }}
                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:bg-red-500/10"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete post
                            </button>
                        )}

                        {(showDelete || showPin) && hasSubsequentItems && (
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        )}

                        {!!feedTuning && (
                            <>
                                <button
                                    disabled={feedTuning.busy}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={e => {
                                        e.stopPropagation()
                                        setShowMenu(false)
                                        feedTuning.onShowMore()
                                    }}
                                >
                                    <Smile className="w-4 h-4 shrink-0" />
                                    {strings.posts.showMoreLikeThis}
                                </button>
                                <button
                                    disabled={feedTuning.busy}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={e => {
                                        e.stopPropagation()
                                        setShowMenu(false)
                                        feedTuning.onShowLess()
                                    }}
                                >
                                    <Frown className="w-4 h-4 shrink-0" />
                                    {strings.posts.showLessLikeThis}
                                </button>
                            </>
                        )}

                        {!!feedTuning && hasCopyOrExploreItems && (
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        )}

                        {!!text && (
                            <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                onClick={e => {
                                    e.stopPropagation()
                                    navigator.clipboard
                                        .writeText(text)
                                        .then(() => showToast(strings.posts.textCopied, 'success'))
                                        .catch(() =>
                                            showToast(strings.posts.couldNotCopyText, 'error')
                                        )
                                    setShowMenu(false)
                                }}
                            >
                                <Copy className="w-4 h-4 shrink-0" />
                                {strings.posts.copyText}
                            </button>
                        )}

                        {showCopyUri && (
                            <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                onClick={e => {
                                    e.stopPropagation()
                                    navigator.clipboard
                                        .writeText(uri ?? '')
                                        .then(() => showToast(strings.posts.uriCopied, 'success'))
                                        .catch(() =>
                                            showToast(strings.posts.couldNotCopyUri, 'error')
                                        )
                                    setShowMenu(false)
                                }}
                            >
                                <Copy className="w-4 h-4 shrink-0" />
                                {strings.posts.copyUri}
                            </button>
                        )}

                        {showAtExplore && (
                            <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                onClick={handleAtExploreClick}
                            >
                                <ExternalLink className="w-4 h-4 shrink-0" />
                                <span className="whitespace-nowrap">
                                    {strings.posts.openInAtExplore}
                                </span>
                            </button>
                        )}

                        {!!nostrNevent && (
                            <>
                                <button
                                    onClick={e => {
                                        e.stopPropagation()
                                        setShowMenu(false)
                                        navigator.clipboard
                                            .writeText(nostrNevent)
                                            .then(() => showToast('Event ID copied', 'success'))
                                            .catch(() => showToast('Failed to copy', 'error'))
                                    }}
                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-border/10"
                                >
                                    <Copy className="w-4 h-4" />
                                    Copy event ID
                                </button>
                                <a
                                    href={`https://njump.me/${nostrNevent}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => {
                                        e.stopPropagation()
                                        setShowMenu(false)
                                    }}
                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-border/10"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    View on njump.me
                                </a>
                            </>
                        )}
                    </div>
                </>
            )}

            <DeletePostConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={() => {
                    onDelete?.()
                    setShowDeleteConfirm(false)
                }}
            />
        </div>
    )
}
